import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { LinkSlackIdentityCommandInput } from '@amzn/gbsa-orbit-typescript-client';
import { userPreferencesService } from 'src/data/api/orbit-service/userPreferencesService';
import { userPreferencesService as userPreferencesServiceV2 } from 'src/data/api/orbit-service-ts/user-preferences/userPreferencesService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  UserPreferencesDetails,
  GetUserPreferencesRequest,
  UpdateUserPreferencesRequest,
} from 'src/types/orbit/UserPreferences';
import { handleError } from 'src/utils/generalUtils';

export interface UserPreferencesState {
  userPreferences?: UserPreferencesDetails;
  getUserPreferencesApiStatus: ApiRequestStatus;
  updateUserPreferencesApiStatus: ApiRequestStatus;
  linkSlackIdentityApiStatus: ApiRequestStatus;
}

const initialState: UserPreferencesState = {
  getUserPreferencesApiStatus: initApiStatus,
  updateUserPreferencesApiStatus: initApiStatus,
  linkSlackIdentityApiStatus: initApiStatus,
};

export const getUserPreferencesApiThunk = createAsyncThunk(
  'userPreferences/getUserPreferences',
  async (data: GetUserPreferencesRequest, { rejectWithValue }) => {
    try {
      const response = await userPreferencesService.get(data);
      return response.preferences;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateUserPreferencesApiThunk = createAsyncThunk(
  'userPreferences/updateUserPreferences',
  async (data: UpdateUserPreferencesRequest, { rejectWithValue }) => {
    try {
      const response = await userPreferencesService.update(data);
      return {
        preferences: response.preferences,
        message: 'User preferences updated successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const linkSlackIdentityApiThunk = createAsyncThunk(
  'userPreferences/linkSlackIdentity',
  async (data: LinkSlackIdentityCommandInput, { rejectWithValue }) => {
    try {
      const response = await userPreferencesServiceV2.linkSlackIdentity(data);
      const { slackIdentity } = response;
      if (!slackIdentity) {
        return undefined;
      }
      // Normalize the generated client's Date to an epoch number so both the
      // dispatched action and the store hold only serializable values.
      return {
        ...slackIdentity,
        linkedAt:
          slackIdentity.linkedAt instanceof Date
            ? slackIdentity.linkedAt.getTime()
            : slackIdentity.linkedAt,
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    clearUserPreferencesMessages: (state) => {
      state.updateUserPreferencesApiStatus.errorMessage = '';
      state.updateUserPreferencesApiStatus.successMessage = '';
      state.linkSlackIdentityApiStatus.errorMessage = '';
      state.linkSlackIdentityApiStatus.successMessage = '';
    },
    resetUpdateApiStatus: (state) => {
      state.updateUserPreferencesApiStatus.isSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserPreferencesApiThunk.pending, (state) => {
        state.getUserPreferencesApiStatus.isLoading = true;
        state.getUserPreferencesApiStatus.errorMessage = null;
      })
      .addCase(getUserPreferencesApiThunk.fulfilled, (state, action) => {
        state.getUserPreferencesApiStatus.isLoading = false;
        state.userPreferences = action.payload;
      })
      .addCase(getUserPreferencesApiThunk.rejected, (state, action) => {
        state.getUserPreferencesApiStatus.isLoading = false;
        state.userPreferences = undefined;
        state.getUserPreferencesApiStatus.errorMessage = action.payload as string;
      })

      .addCase(updateUserPreferencesApiThunk.pending, (state) => {
        state.updateUserPreferencesApiStatus.isLoading = true;
        state.updateUserPreferencesApiStatus.errorMessage = null;
        state.updateUserPreferencesApiStatus.successMessage = null;
        state.updateUserPreferencesApiStatus.isSuccess = false;
      })
      .addCase(updateUserPreferencesApiThunk.fulfilled, (state, action) => {
        state.updateUserPreferencesApiStatus.isLoading = false;
        state.updateUserPreferencesApiStatus.isSuccess = true;
        state.updateUserPreferencesApiStatus.successMessage = action.payload.message;
        state.userPreferences = action.payload.preferences;
      })
      .addCase(updateUserPreferencesApiThunk.rejected, (state, action) => {
        state.updateUserPreferencesApiStatus.isLoading = false;
        state.updateUserPreferencesApiStatus.isSuccess = false;
        state.updateUserPreferencesApiStatus.errorMessage = action.payload as string;
        state.updateUserPreferencesApiStatus.successMessage = null;
      })

      .addCase(linkSlackIdentityApiThunk.pending, (state) => {
        state.linkSlackIdentityApiStatus.isLoading = true;
        state.linkSlackIdentityApiStatus.errorMessage = null;
        state.linkSlackIdentityApiStatus.successMessage = null;
        state.linkSlackIdentityApiStatus.isSuccess = false;
      })
      .addCase(linkSlackIdentityApiThunk.fulfilled, (state, action) => {
        state.linkSlackIdentityApiStatus.isLoading = false;
        state.linkSlackIdentityApiStatus.isSuccess = true;
        state.linkSlackIdentityApiStatus.successMessage =
          'Slack account linked successfully';
        if (state.userPreferences && action.payload) {
          state.userPreferences.slackIdentity = action.payload;
        }
      })
      .addCase(linkSlackIdentityApiThunk.rejected, (state, action) => {
        state.linkSlackIdentityApiStatus.isLoading = false;
        state.linkSlackIdentityApiStatus.isSuccess = false;
        state.linkSlackIdentityApiStatus.errorMessage = action.payload as string;
        state.linkSlackIdentityApiStatus.successMessage = null;
      });
  },
});

export const { clearUserPreferencesMessages, resetUpdateApiStatus } =
  userPreferencesSlice.actions;
export default userPreferencesSlice.reducer;
